import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Edit3, Trash2 } from "lucide-react";
import { SCField, SCModal, SCSection, SCEmpty, useConfirm, fmtDate } from "./Common";
import {
    scAdminListAmcs, scAdminCreateAmc, scAdminPatchAmc, scAdminDeleteAmc,
} from "../../../lib/sifcontrolApi";

const empty = () => ({
    amc_name: "", short_name: "", website: "", logo: "",
    contact_name: "", contact_email: "", status: "active",
});

export default function AmcsTab() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(null); // {mode:'create'|'edit', data}
    const [saving, setSaving] = useState(false);
    const { confirm, dialog } = useConfirm();

    const load = async () => {
        setLoading(true);
        try {
            const r = await scAdminListAmcs();
            setItems(r.items || []);
        } catch (e) {
            toast.error("Could not load AMCs");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const openCreate = () => setModal({ mode: "create", data: empty() });
    const openEdit = (row) => setModal({ mode: "edit", data: { ...row } });

    const save = async () => {
        const d = modal.data;
        if (!d.amc_name || d.amc_name.trim().length < 2) {
            toast.error("AMC name is required");
            return;
        }
        setSaving(true);
        try {
            if (modal.mode === "create") await scAdminCreateAmc(d);
            else await scAdminPatchAmc(d.id, {
                amc_name: d.amc_name, short_name: d.short_name, website: d.website,
                logo: d.logo, contact_name: d.contact_name, contact_email: d.contact_email,
                status: d.status,
            });
            toast.success(modal.mode === "create" ? "AMC added" : "AMC updated");
            setModal(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const del = async (row) => {
        const ok = await confirm(`Delete "${row.amc_name}"? This can't be undone.`);
        if (!ok) return;
        try {
            await scAdminDeleteAmc(row.id);
            toast.success("AMC deleted");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        }
    };

    return (
        <div data-testid="sc-admin-amcs">
            <SCSection
                title="AMCs"
                subtitle="Asset Management Companies registered with SIF Control."
                right={
                    <button onClick={openCreate} className="btn-pill btn-pill-dark !text-xs !py-1.5 !px-3" data-testid="sc-amc-add-btn">
                        <Plus size={14} /> Add AMC
                    </button>
                }
                testId="sc-amcs-header"
            />

            {loading ? (
                <div className="p-10 text-center text-sm font-body text-oth-ink3">Loading…</div>
            ) : items.length === 0 ? (
                <SCEmpty
                    message="No AMCs registered yet."
                    cta={<button onClick={openCreate} className="btn-pill btn-pill-dark !text-xs" data-testid="sc-amc-empty-cta">+ Add your first AMC</button>}
                    testId="sc-amcs-empty"
                />
            ) : (
                <div className="rounded-2xl border border-oth-line/70 bg-white overflow-hidden">
                    <div className="grid grid-cols-[1.6fr_120px_90px_90px_100px] text-[10px] uppercase tracking-[0.16em] text-oth-ink3 font-body px-5 py-3 bg-oth-bg-2/60 border-b border-oth-line/70">
                        <div>AMC</div>
                        <div>Short name</div>
                        <div>SIFs</div>
                        <div>Status</div>
                        <div className="text-right">Actions</div>
                    </div>
                    <div className="divide-y divide-oth-line/60" data-testid="sc-amcs-list">
                        {items.map((a) => (
                            <div key={a.id} className="grid grid-cols-[1.6fr_120px_90px_90px_100px] items-center px-5 py-3.5" data-testid={`sc-amc-row-${a.id}`}>
                                <div>
                                    <div className="font-display text-sm font-medium text-oth-ink">{a.amc_name}</div>
                                    <div className="text-[11px] font-body text-oth-ink3 mt-0.5">Created {fmtDate(a.created_at)}</div>
                                </div>
                                <div className="text-xs font-body text-oth-ink2">{a.short_name || "—"}</div>
                                <div className="text-xs font-body text-oth-ink2">{a.sif_count ?? 0}</div>
                                <div className="text-xs font-body">
                                    <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider ${a.status === "active" ? "bg-green-100 text-green-800" : "bg-oth-bg-2 text-oth-ink3"}`}>{a.status}</span>
                                </div>
                                <div className="flex items-center justify-end gap-1">
                                    <button onClick={() => openEdit(a)} className="w-8 h-8 rounded-full hover:bg-oth-bg-2 flex items-center justify-center" data-testid={`sc-amc-edit-${a.id}`} aria-label="Edit"><Edit3 size={13} /></button>
                                    <button onClick={() => del(a)} className="w-8 h-8 rounded-full hover:bg-red-50 text-oth-ink3 hover:text-red-700 flex items-center justify-center" data-testid={`sc-amc-delete-${a.id}`} aria-label="Delete"><Trash2 size={13} /></button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <SCModal
                open={!!modal}
                title={modal?.mode === "create" ? "Add AMC" : "Edit AMC"}
                onClose={() => !saving && setModal(null)}
                testId="sc-amc-modal"
                footer={
                    <>
                        <button onClick={() => setModal(null)} disabled={saving} className="btn-pill !text-sm" data-testid="sc-amc-modal-cancel">Cancel</button>
                        <button onClick={save} disabled={saving} className="btn-pill btn-pill-dark !text-sm" data-testid="sc-amc-modal-save">{saving ? "Saving…" : "Save"}</button>
                    </>
                }
            >
                {modal && (
                    <div>
                        <SCField label="AMC name" value={modal.data.amc_name} onChange={(v) => setModal({ ...modal, data: { ...modal.data, amc_name: v } })} testId="sc-amc-form-name" required />
                        <SCField label="Short name" value={modal.data.short_name} onChange={(v) => setModal({ ...modal, data: { ...modal.data, short_name: v } })} testId="sc-amc-form-short" hint="Optional. Used in compact table columns." />
                        <SCField label="Website" value={modal.data.website} onChange={(v) => setModal({ ...modal, data: { ...modal.data, website: v } })} testId="sc-amc-form-website" placeholder="https://…" />
                        <SCField label="Logo URL" value={modal.data.logo} onChange={(v) => setModal({ ...modal, data: { ...modal.data, logo: v } })} testId="sc-amc-form-logo" hint="Direct image URL (PNG/SVG)." />
                        <div className="grid grid-cols-2 gap-3">
                            <SCField label="Contact name" value={modal.data.contact_name} onChange={(v) => setModal({ ...modal, data: { ...modal.data, contact_name: v } })} testId="sc-amc-form-contact-name" />
                            <SCField label="Contact email" value={modal.data.contact_email} onChange={(v) => setModal({ ...modal, data: { ...modal.data, contact_email: v } })} testId="sc-amc-form-contact-email" type="email" />
                        </div>
                        <SCField label="Status" value={modal.data.status} onChange={(v) => setModal({ ...modal, data: { ...modal.data, status: v } })} type="select" options={["active", "inactive"]} testId="sc-amc-form-status" />
                    </div>
                )}
            </SCModal>
            {dialog}
        </div>
    );
}
